import { z } from 'zod';
import { BackgroundType, LIMITS } from './constants.js';

// ─── Reusable Schemas ───────────────────────────────────────────

const giftItemSchema = z.object({
    title: z.string().min(1).max(LIMITS.MAX_TITLE_LENGTH),
    imageUrl: z.string().url(),
    productUrl: z.string().url().optional().or(z.literal('')),
    requestSize: z.boolean().default(false),
    requestColor: z.boolean().default(false),
    requestNote: z.boolean().default(false),
});

const shippingSchema = z.object({
    fullName: z.string().min(2).max(100),
    street: z.string().min(3).max(200),
    city: z.string().min(2).max(100),
    zip: z.string().min(3).max(20),
    phone: z
        .string()
        .min(7)
        .max(20)
        .regex(/^[+\d\s\-()]+$/, 'Invalid phone number format'),
    email: z.string().email(),
});

// ─── Create Gift ────────────────────────────────────────────────

const giftBrandingSchema = z.object({
    logoUrl: z.string().url(),
    companyName: z.string().min(1).max(100),
    primaryColor: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Must be a valid hex color'),
});

export const createGiftSchema = z.object({
    title: z.string().min(1).max(LIMITS.MAX_TITLE_LENGTH),
    description: z.string().max(LIMITS.MAX_DESCRIPTION_LENGTH).optional().or(z.literal('')),
    creatorEmail: z.string().email(),
    backgroundType: z.nativeEnum(BackgroundType).default('GENERAL'),
    items: z
        .array(giftItemSchema)
        .min(LIMITS.MIN_ITEMS, `At least ${LIMITS.MIN_ITEMS} items required`)
        .max(LIMITS.MAX_ITEMS, `At most ${LIMITS.MAX_ITEMS} items allowed`),
    branding: giftBrandingSchema.optional(),
});

// ─── Choose Gift ────────────────────────────────────────────────

export const chooseGiftSchema = z.object({
    itemIndex: z.number().int().min(0),
    options: z
        .object({
            size: z.string().max(50).optional(),
            color: z.string().max(50).optional(),
            note: z.string().max(500).optional(),
        })
        .optional(),
    shipping: shippingSchema,
});

// ─── Send Thank You ─────────────────────────────────────────────

export const sendThankYouSchema = z.object({
    message: z.string().min(1, 'Wiadomość nie może być pusta').max(1000, 'Wiadomość jest zbyt długa'),
    imageUrl: z.string().url('Nieprawidłowy adres URL zdjęcia').optional(),
});

// ─── Delete Gift ────────────────────────────────────────────────

export const deleteGiftSchema = z.object({
    deleteToken: z.string().min(1),
});

// ─── Presign Upload ─────────────────────────────────────────────

export const presignUploadSchema = z.object({
    filename: z.string().min(1).max(255),
    contentType: z
        .string()
        .regex(/^image\/(jpeg|png|webp|gif)$/, 'Only JPEG, PNG, WebP, and GIF images are allowed'),
});

// ─── Widget Session ─────────────────────────────────────────────

export const createWidgetSessionSchema = z.object({
    companyId: z.string().min(1).max(100),
    items: z
        .array(giftItemSchema)
        .min(1)
        .max(LIMITS.MAX_ITEMS),
});

export const finalizeWidgetSessionSchema = z.object({
    creatorEmail: z.string().email(),
    title: z.string().min(1).max(LIMITS.MAX_TITLE_LENGTH),
    description: z.string().max(LIMITS.MAX_DESCRIPTION_LENGTH).optional().or(z.literal('')),
    backgroundType: z.nativeEnum(BackgroundType).default('GENERAL'),
});

// ─── Metadata Parse ─────────────────────────────────────────────

export const parseMetadataSchema = z.object({
    url: z.string().url(),
});

// ─── Exports ────────────────────────────────────────────────────

export { giftItemSchema, shippingSchema };
